if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      variantRequestSequence = 0;
      lastVariantRequest = null;
      variantRetryButton = null;
      quantityRequestSequence = 0;
      quantityAbortController = null;
      swapUtilityInitialized = false;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
        if (this.dataset.variantState === 'loading' && this.lastVariantRequest) {
          this.renderProductInfo(this.lastVariantRequest);
        }
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.variantRequestSequence += 1;
        this.abortController?.abort();
        this.quantityRequestSequence += 1;
        this.quantityAbortController?.abort();
        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden');
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        if (this.swapUtilityInitialized) return;
        this.swapUtilityInitialized = true;
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          swappingProduct: shouldSwapProduct,
          fullPage: shouldFetchFullPage,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        this.quantityRequestSequence += 1;
        this.quantityAbortController?.abort();
        this.querySelector('.quantity__rules-cart .loading__spinner')?.classList.add('hidden');
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
        this.variantRetryButton?.remove();
        this.variantRetryButton = null;
        this.dataset.variantState = 'loading';
        productForm?.setAttribute('aria-busy', 'true');
        this.updateVariantInputs(null);
        this.querySelectorAll('.shopify-payment-button').forEach((button) => {
          button.inert = true;
        });
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      async renderProductInfo(request) {
        const { requestUrl, targetId, callback } = request;
        this.lastVariantRequest = request;
        const requestSequence = ++this.variantRequestSequence;
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;

        try {
          const response = await fetch(requestUrl, { signal: controller.signal });
          if (!response.ok) throw new Error(`Product response ${response.status}`);
          const responseText = await response.text();
          if (requestSequence !== this.variantRequestSequence || controller.signal.aborted || !this.isConnected) return;

          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const productInfo = request.fullPage
            ? html.querySelector("product-info[id^='MainProduct']")
            : html.querySelector('product-info');
          if (!productInfo) throw new Error('Missing product section');
          const variantScript = productInfo.querySelector('variant-selects [data-selected-variant]');
          if (this.variantSelectors && !variantScript && !request.swappingProduct) {
            throw new Error('Missing product selection');
          }
          if (variantScript) JSON.parse(variantScript.textContent);
          const selectedVariant = this.getSelectedVariant(productInfo);
          const variantInput = productInfo.querySelector('product-form input[name="id"]');
          if (request.swappingProduct && !variantScript && this.productForm && !variantInput?.value) {
            throw new Error('Missing product variant');
          }
          if (selectedVariant && this.productForm && String(selectedVariant.id) !== variantInput?.value) {
            throw new Error('Product selection mismatch');
          }
          const activeElement = document.activeElement;
          const restoreSelectionFocus = activeElement === document.body ||
            activeElement === document.documentElement || this.variantSelectors?.contains(activeElement) ||
            activeElement === this.variantRetryButton;
          this.pendingRequestUrl = null;
          callback(html);
          this.dataset.variantState = 'ready';
          this.lastVariantRequest = null;
          this.querySelectorAll('.shopify-payment-button').forEach((button) => {
            button.inert = !selectedVariant?.available || Boolean(this.productForm?.submitButton.disabled);
          });
          if (restoreSelectionFocus) document.getElementById(targetId)?.focus({ preventScroll: true });
        } catch (error) {
          if (requestSequence !== this.variantRequestSequence || controller.signal.aborted || !this.isConnected) return;
          this.pendingRequestUrl = null;
          this.dataset.variantState = 'error';
          this.updateVariantInputs(null);
          this.productForm?.toggleSubmitButton(true);
          this.productForm?.handleErrorMessage(window.cartStrings.error);
          this.variantSelectors?.rejectPendingSelectPromise(error);
          const errorWrapper = this.productForm?.querySelector('.product-form__error-message-wrapper');
          if (errorWrapper && window.variantStrings.retry) {
            this.variantRetryButton = document.createElement('button');
            this.variantRetryButton.type = 'button';
            this.variantRetryButton.className = 'link underlined-link fuji-variant-retry';
            this.variantRetryButton.textContent = window.variantStrings.retry;
            this.variantRetryButton.addEventListener('click', () => {
              if (!this.lastVariantRequest) return;
              const retryRequest = this.lastVariantRequest;
              this.resetProductFormState();
              this.renderProductInfo(retryRequest);
            });
            errorWrapper.append(this.variantRetryButton);
          }
        } finally {
          if (requestSequence === this.variantRequestSequence) {
            this.productForm?.removeAttribute('aria-busy');
          }
        }
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const requestUrl = new URL(url, window.location.origin);
        requestUrl.searchParams.delete('variant');
        if (!shouldFetchFullPage) requestUrl.searchParams.set('section_id', this.sectionId);
        else requestUrl.searchParams.delete('section_id');
        requestUrl.searchParams.delete('option_values');
        if (optionValues.length) requestUrl.searchParams.set('option_values', optionValues.join(','));
        return `${requestUrl.pathname}${requestUrl.search}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const currentRelease = this.productForm?.closest('fuji-release');
          const sourceRelease = html.querySelector('fuji-release[data-release-scope="product"]');
          if (currentRelease && sourceRelease && currentRelease.dataset.releaseBlocked !== sourceRelease.dataset.releaseBlocked) {
            currentRelease.replaceFrom(sourceRelease);
            window.Shopify?.PaymentButton?.init();
          } else if (currentRelease && sourceRelease) {
            currentRelease.syncState(sourceRelease);
          }
          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.querySelector('span')?.textContent.trim() || window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.disabled = !variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const updatedUrl = new URL(url, window.location.origin);
        updatedUrl.searchParams.delete('option_values');
        updatedUrl.searchParams.delete('section_id');
        if (variantId) updatedUrl.searchParams.set('variant', variantId);
        else updatedUrl.searchParams.delete('variant');
        this.querySelector('share-button')?.updateUrl(updatedUrl.href);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${updatedUrl.pathname}${updatedUrl.search}${updatedUrl.hash}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector('media-gallery ul')?.cloneNode(true);
        if (mediaGalleryDestination && this.dataset.originalSection) {
          [mediaGalleryDestination, ...mediaGalleryDestination.querySelectorAll('*')].forEach((element) => {
            Array.from(element.attributes).forEach(({ name, value }) => {
              if (value.includes(this.sectionId)) {
                element.setAttribute(name, value.replaceAll(this.sectionId, this.dataset.section));
              }
            });
          });
        }
        const fallbackMediaId = mediaGalleryDestination?.querySelector('li[data-media-id].is-active')?.dataset.mediaId ||
          mediaGalleryDestination?.querySelector('li[data-media-id]')?.dataset.mediaId;

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        const activeMediaId = variantFeaturedMediaId
          ? `${this.dataset.section}-${variantFeaturedMediaId}`
          : fallbackMediaId;
        if (activeMediaId) this.querySelector('media-gallery')?.setActiveMedia?.(activeMediaId, true);

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? null : Math.max(0, data.max - data.cartQuantity);
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max !== null) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const requestSequence = ++this.quantityRequestSequence;
        this.quantityAbortController?.abort();
        const controller = new AbortController();
        this.quantityAbortController = controller;
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        const requestUrl = new URL(this.dataset.url, window.location.origin);
        requestUrl.searchParams.set('variant', currentVariantId);
        requestUrl.searchParams.set('section_id', this.sectionId);
        return fetch(`${requestUrl.pathname}${requestUrl.search}`, { signal: controller.signal })
          .then((response) => {
            if (!response.ok) throw new Error(`Quantity response ${response.status}`);
            return response.text();
          })
          .then((responseText) => {
            if (!this.isConnected || controller.signal.aborted || requestSequence !== this.quantityRequestSequence ||
              this.productForm?.variantIdInput?.value !== currentVariantId) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.sectionId, html);
          })
          .catch((error) => {
            if (!controller.signal.aborted && requestSequence === this.quantityRequestSequence) console.error(error);
          })
          .finally(() => {
            if (requestSequence === this.quantityRequestSequence) spinner?.classList.add('hidden');
          });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated || !this.quantityForm) return;
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
